import axios from "axios";

const API_URL = process.env.VUE_APP_API_URL || "http://127.0.0.1:5001";

class ApiService {
  getAuthToken() {
    try {
      return localStorage.getItem("onlygrades_auth_token") || "";
    } catch {
      return "";
    }
  }

  authHeaders() {
    const token = this.getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  loginWithGoogle(idToken) {
    return axios.post(`${API_URL}/auth/google`, { id_token: idToken });
  }

  getMe() {
    return axios.get(`${API_URL}/auth/me`, { headers: this.authHeaders() });
  }

  logout() {
    return axios.post(
      `${API_URL}/auth/logout`,
      {},
      { headers: this.authHeaders() }
    );
  }

  getBillingStatus() {
    return axios.get(`${API_URL}/billing/status`, {
      headers: this.authHeaders(),
    });
  }

  createCheckoutSession() {
    return axios.post(
      `${API_URL}/billing/create-checkout-session`,
      {},
      { headers: this.authHeaders() }
    );
  }

  getPassedCourses() {
    return axios.get(`${API_URL}/me/passed_courses`, {
      headers: this.authHeaders(),
    });
  }

  getStudyPlanCourses() {
    return axios.get(`${API_URL}/me/study_plan`, {
      headers: this.authHeaders(),
    });
  }

  addStudyPlanCourse(courseCode) {
    return axios.post(
      `${API_URL}/me/study_plan/course`,
      { course_code: courseCode },
      { headers: this.authHeaders() }
    );
  }

  removeStudyPlanCourse(courseCode) {
    return axios.delete(`${API_URL}/me/study_plan/course`, {
      data: { course_code: courseCode },
      headers: this.authHeaders(),
    });
  }

  setPassedCourses(courseCodes) {
    return axios.put(
      `${API_URL}/me/passed_courses`,
      { course_codes: courseCodes },
      { headers: this.authHeaders() }
    );
  }

  getMyDataExport() {
    return axios.get(`${API_URL}/me/data_export`, {
      headers: this.authHeaders(),
    });
  }

  deleteMyAccount(confirmDelete = false) {
    return axios.delete(`${API_URL}/me`, {
      headers: this.authHeaders(),
      data: { confirm: Boolean(confirmDelete) },
    });
  }

  getProgramAvailableSemesters(studyProgramCode, lookbackYears = 3) {
    return axios.get(
      `${API_URL}/study_programs/${studyProgramCode}/available_semesters`,
      { params: { lookback_years: lookbackYears } }
    );
  }

  getPlannerRecommendations(payload) {
    return axios.post(`${API_URL}/planner/recommendations`, payload, {
      headers: this.authHeaders(),
    });
  }

  getPlannerInsights(payload) {
    return axios.post(`${API_URL}/planner/insights`, payload, {
      headers: this.authHeaders(),
    });
  }

  // Get a list of courses
  getCourses(options = {}) {
    const params = {};
    if (options.recentOnly) {
      params.recent_only = 1;
    }
    if (options.includeLegacyArrays === false) {
      params.include_legacy_arrays = 0;
    }
    if (Number.isInteger(options.lookbackYears) && options.lookbackYears > 0) {
      params.lookback_years = options.lookbackYears;
    }
    return axios.get(`${API_URL}/courses`, { params });
  }

  // Get detailed information about a specific course by course_code
  getCourseDetails(courseCode) {
    return axios.get(`${API_URL}/course/${courseCode}`);
  }

  getAggregatedCourseStats(courseCode) {
    return axios.get(`${API_URL}/course/${courseCode}/aggregated_stats`);
  }

  getCourseProgramStats(courseCode) {
    return axios.get(`${API_URL}/course/${courseCode}/program_stats`);
  }

  // Get course offerings by course_code
  getCourseOfferings(courseCode) {
    return axios.get(`${API_URL}/course/${courseCode}/offerings`);
  }

  // Get offering grades by offering_id
  getOfferingGrades(offeringId) {
    return axios.get(`${API_URL}/offering/${offeringId}/grades`);
  }

  // Get top courses based on a statistic and a limit
  getTopCourses(statistic, limit) {
    return axios.get(`${API_URL}/top_courses/${statistic}/${limit}`);
  }

  // Get overall adjusted difficulty statistics
  getOverallAdjustedDifficulty() {
    return axios.get(`${API_URL}/statistics/adjusted_difficulty`);
  }

  // Get common courses for a specific year
  getCommonCourses(year) {
    return axios.get(`${API_URL}/statistics/common_courses/${year}`);
  }

  // Get course performance by course_code
  getCoursePerformance(courseCode) {
    return axios.get(`${API_URL}/course/${courseCode}/performance`);
  }

  getInstitutions() {
    return axios.get(`${API_URL}/institutions`);
  }

  getCourseCatalog(params, signal) {
    return axios.get(`${API_URL}/course_catalog`, {
      params,
      signal,
      timeout: 30000,
      headers: this.authHeaders(),
    });
  }

  getStudyPrograms(institutionCode) {
    return axios.get(
      `${API_URL}/institutions/${institutionCode}/study_programs`
    );
  }

  getEasiestCourses(institutionCode, studyProgramCode = "") {
    let url = `${API_URL}/easiest_courses/${institutionCode}`;
    if (studyProgramCode) {
      url += `/${studyProgramCode}`;
    }
    return axios.get(url, { headers: this.authHeaders() });
  }

  getHardestCourses(institutionCode, studyProgramCode = "") {
    let url = `${API_URL}/hardest_courses/${institutionCode}`;
    if (studyProgramCode) {
      url += `/${studyProgramCode}`;
    }
    return axios.get(url, { headers: this.authHeaders() });
  }

  joinPremiumWaitlist(payload) {
    return axios.post(`${API_URL}/waitlist/signup`, payload);
  }
}

export default new ApiService();
